import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { getIdToken } from '../attendedUtils';
import { useIsMobile } from '../hooks/useMediaQuery';
import MobileTabSelect from './MobileTabSelect';
import ShowArchiveCard, { ShowArchiveCardList, cardControl, joinLocation } from './ShowArchiveCard';
import { normalizeStr, JumpPagination, ShowsByYearTab, VenuesVisitedTab, ArtistsWatchedTab, ShowsMapTab } from './ShowStatsTabs';
import { FollowersFollowingTab } from './FollowersFollowingTab';
import FlyerHistoryTabs from './FlyerHistoryTabs';

// My Shows Redesign (2026-09-22) — rebuilt onto ArtistDetail.js's Section 1/2/3
// layout. Full spec: SHARC_MyShows_Redesign_Spec_2026-09-22.docx.
//   Section 1: display name + View Public Profile / Edit Profile
//   Section 2: initials box + location + Member Record (left);
//              Main/Show/Location/Yearly Stats info tabs (right)
//   Section 3: Show Archive / Shows by Year / Artists Watched / Venues Visited /
//              Locations (Shows Map, Geographic Breakdown) / Flyers / Followers
// Top Cities was retired in favor of Locations → Geographic Breakdown.

const INFO_TABS = ['Main Stats', 'Show Stats', 'Location Stats', 'Yearly Stats'];
const PARENT_TABS = ['Show Archive', 'Shows by Year', 'Artists Watched', 'Venues Visited', 'Locations', 'Flyers', 'Followers'];
const LOCATIONS_SUBTABS = ['Shows Map', 'Geographic Breakdown'];
const PAGE_SIZE = 25;

const fmtNum = (n) => (parseInt(n) || 0).toLocaleString('en-US');

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
};

// Same Years/Months/Days breakdown as ArtistDetail.js's Show Stats tab.
function computeTerm(firstDate, lastDate) {
    if (!firstDate || !lastDate) return '—';
    const d1 = new Date(firstDate);
    const d2 = new Date(lastDate);
    let years = d2.getUTCFullYear() - d1.getUTCFullYear();
    let months = d2.getUTCMonth() - d1.getUTCMonth();
    let days = d2.getUTCDate() - d1.getUTCDate();
    if (days < 0) { months -= 1; days += new Date(d2.getUTCFullYear(), d2.getUTCMonth(), 0).getDate(); }
    if (months < 0) { years -= 1; months += 12; }
    const parts = [];
    if (years > 0) parts.push(`${years} ${years === 1 ? 'Year' : 'Years'}`);
    if (months > 0) parts.push(`${months} ${months === 1 ? 'Month' : 'Months'}`);
    if (days > 0) parts.push(`${days} ${days === 1 ? 'Day' : 'Days'}`);
    return parts.join(' ') || '—';
}

// ── Sub-tab bar (ArtistDetail.js) ─────────────────────────────────────────────
// Mobile (2026-10-28): a single dropdown instead of a scrolling button row.
function SubTabBar({ tabs, active, onChange }) {
    const isMobile = useIsMobile();
    if (isMobile) return <MobileTabSelect variant="sub" tabs={tabs} active={active} onChange={onChange} />;
    return (
        <div style={styles.subTabBar}>
            {tabs.map((tab, i) => (
                <button
                    key={tab}
                    onClick={() => onChange(i)}
                    style={{ ...styles.subTabBtn, ...(active === i ? styles.subTabBtnActive : {}) }}
                >
                    {tab}
                </button>
            ))}
        </div>
    );
}

// ── Stat box grid (header-right info tabs) ────────────────────────────────────
function StatBoxGrid({ items, isMobile }) {
    const cols = isMobile ? 2 : items.length;
    return (
        <div style={{ ...styles.statGrid, gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
            {items.map(item => (
                <div key={item.label} style={styles.statBox}>
                    <div style={isMobile ? { ...styles.statValue, ...Smobile.statValue } : styles.statValue}>{item.value}</div>
                    <div style={styles.statLabel}>{item.label}</div>
                </div>
            ))}
        </div>
    );
}

// ── Show Archive Tab (formerly Show History) ──────────────────────────────────
// Same columns, search, and server-assigned show_number as before; pagination
// switched to jump-to-page (spec Decision 9).
function ShowArchiveTab({ shows }) {
    const isMobile = useIsMobile();
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');

    if (!shows || shows.length === 0) return <div style={styles.emptyTab}>No shows recorded yet. Start marking events as Attended!</div>;

    const q = normalizeStr(search);
    const filtered = search ? shows.filter(s =>
        [s.event_date ? new Date(s.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '',
         s.city, s.state, s.country, s.venue_name, s.artists, s.festival_name, s.role]
            .some(v => v && normalizeStr(v).includes(q))
    ) : shows;

    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <div>
            <input
                type="text"
                placeholder="Search by date, city, venue, artists, role..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                style={styles.tabSearch}
            />
            {filtered.length === 0 ? (
                <div style={styles.emptyTab}>No shows match your search.</div>
            ) : (
                <>
                    {isMobile ? (
                        <ShowArchiveCardList>
                            {paginated.map(show => {
                                const isPerformed = show.role === 'Performed';
                                const isWorked = show.role === 'Worked';
                                return (
                                    <ShowArchiveCard
                                        key={show.event_id}
                                        title={`Show #${show.show_number}`}
                                        titleTo={`/events/${show.event_slug || show.event_id}`}
                                        date={show.event_date}
                                        venueName={show.venue_name}
                                        venueTo={show.venue_id ? `/venues/${show.venue_slug || show.venue_id}` : null}
                                        location={joinLocation(show.city, show.state, show.country)}
                                        festivalName={show.festival_name}
                                        festivalTo={show.festival_slug ? `/festivals/${show.festival_slug}` : null}
                                        lineup={show.artists}
                                        footer={
                                            <span style={{ ...(isPerformed ? styles.rolePerformed : isWorked ? styles.roleWorked : styles.roleAttended), ...cardControl }}>
                                                {show.role}
                                            </span>
                                        }
                                    />
                                );
                            })}
                        </ShowArchiveCardList>
                    ) : (
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>#</th>
                                    <th style={styles.th}>Role</th>
                                    <th style={styles.th}>Date</th>
                                    <th style={styles.th}>City</th>
                                    <th style={styles.th}>State</th>
                                    <th style={styles.th}>Country</th>
                                    <th style={styles.th}>Venue</th>
                                    <th style={styles.th}>Artists</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.map((show, idx) => {
                                    const formattedDate = show.event_date
                                        ? new Date(show.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
                                        : '—';
                                    const isPerformed = show.role === 'Performed';
                                    const isWorked = show.role === 'Worked';
                                    return (
                                        <tr key={show.event_id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                            <td style={{ ...styles.td, fontWeight: 'bold' }}>
                                                <Link to={`/events/${show.event_slug || show.event_id}`} style={styles.tableLink}>{show.show_number}</Link>
                                            </td>
                                            <td style={styles.td}>
                                                <span style={isPerformed ? styles.rolePerformed : isWorked ? styles.roleWorked : styles.roleAttended}>
                                                    {show.role}
                                                </span>
                                            </td>
                                            <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>{formattedDate}</td>
                                            <td style={styles.td}>{show.city || '—'}</td>
                                            <td style={styles.td}>{show.state || '—'}</td>
                                            <td style={styles.td}>{show.country || '—'}</td>
                                            <td style={styles.td}>
                                                {show.venue_id
                                                    ? <Link to={`/venues/${show.venue_slug || show.venue_id}`} style={styles.tableLink}>{show.venue_name}</Link>
                                                    : show.venue_name || '—'}
                                            </td>
                                            <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>
                                                {show.festival_name && show.festival_slug
                                                    ? <><Link to={`/festivals/${show.festival_slug}`} style={{ color: '#bb86fc', textDecoration: 'none', fontWeight: 'bold' }}>{show.festival_name}</Link>{show.artists ? ` - ${show.artists}` : ''}</>
                                                    : show.artists || '—'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    )}
                    <JumpPagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

// ── Geographic Breakdown Tab (copied from ArtistDetail.js) ────────────────────
// Country → State → City → Venue rollup, all levels collapsed by default. Fed
// by getMyShows.js's existing `geography` array (same row shape as getArtist).
function GeographicBreakdownTab({ geography }) {
    const [collapsed, setCollapsed] = useState({});

    if (!geography || geography.length === 0) return <div style={styles.emptyTab}>No geographic data available.</div>;

    const rollup = {};
    geography.forEach(row => {
        const country = row.country || 'Unknown';
        const state = row.state || '—';
        const city = row.city || '—';
        const count = parseInt(row.show_count) || 0;
        if (!rollup[country]) rollup[country] = { total: 0, states: {} };
        rollup[country].total += count;
        if (!rollup[country].states[state]) rollup[country].states[state] = { total: 0, cities: {} };
        rollup[country].states[state].total += count;
        if (!rollup[country].states[state].cities[city]) rollup[country].states[state].cities[city] = { total: 0, venues: {} };
        rollup[country].states[state].cities[city].total += count;
        const venueKey = `${row.venue_id}__${row.venue_name || '—'}`;
        if (!rollup[country].states[state].cities[city].venues[venueKey]) {
            rollup[country].states[state].cities[city].venues[venueKey] = { id: row.venue_id, name: row.venue_name || '—', slug: row.venue_slug, count: 0 };
        }
        rollup[country].states[state].cities[city].venues[venueKey].count += count;
    });

    const sortedCountries = Object.entries(rollup).sort((a, b) => b[1].total - a[1].total);
    const toggleKey = (key) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));
    const isOpen = (key) => (key in collapsed ? !collapsed[key] : false);

    return (
        <div>
            {sortedCountries.map(([country, countryData]) => {
                const countryKey = `c:${country}`;
                const countryOpen = isOpen(countryKey);
                const sortedStates = Object.entries(countryData.states).sort((a, b) => b[1].total - a[1].total);
                return (
                    <div key={country} style={styles.geoGroup}>
                        <div style={styles.geoCountryRow} onClick={() => toggleKey(countryKey)}>
                            <span style={styles.geoChevron}>{countryOpen ? '▾' : '▸'}</span>
                            <span style={styles.geoCountryName}>{country}</span>
                            <span style={styles.geoCount}>{countryData.total} show{countryData.total !== 1 ? 's' : ''}</span>
                        </div>
                        {countryOpen && sortedStates.map(([state, stateData]) => {
                            const stateKey = `s:${country}:${state}`;
                            const stateOpen = isOpen(stateKey);
                            const sortedCities = Object.entries(stateData.cities).sort((a, b) => b[1].total - a[1].total);
                            return (
                                <div key={state} style={styles.geoStateBlock}>
                                    <div style={styles.geoStateRow} onClick={() => toggleKey(stateKey)}>
                                        <span style={styles.geoChevron}>{stateOpen ? '▾' : '▸'}</span>
                                        <span style={styles.geoStateName}>{state}</span>
                                        <span style={styles.geoCount}>{stateData.total} show{stateData.total !== 1 ? 's' : ''}</span>
                                    </div>
                                    {stateOpen && sortedCities.map(([city, cityData]) => {
                                        const cityKey = `ci:${country}:${state}:${city}`;
                                        const cityOpen = isOpen(cityKey);
                                        const sortedVenues = Object.values(cityData.venues).sort((a, b) => b.count - a.count);
                                        return (
                                            <div key={city} style={styles.geoCityBlock}>
                                                <div style={styles.geoCityRow} onClick={() => toggleKey(cityKey)}>
                                                    <span style={styles.geoChevron}>{cityOpen ? '▾' : '▸'}</span>
                                                    <span style={styles.geoCityName}>{city}</span>
                                                    <span style={styles.geoCount}>{cityData.total} show{cityData.total !== 1 ? 's' : ''}</span>
                                                </div>
                                                {cityOpen && (
                                                    <div style={styles.geoVenueList}>
                                                        {sortedVenues.map(venue => (
                                                            <div key={venue.id || venue.name} style={styles.geoVenueRow}>
                                                                <span style={styles.geoVenueName}>
                                                                    {venue.id
                                                                        ? <Link to={`/venues/${venue.slug || venue.id}`} style={styles.tableLink}>{venue.name}</Link>
                                                                        : <span style={{ color: '#aaa' }}>{venue.name}</span>}
                                                                </span>
                                                                <span style={styles.showCount}>{venue.count}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                );
            })}
        </div>
    );
}

// ── MyShows ───────────────────────────────────────────────────────────────────
function MyShows() {
    const isMobile = useIsMobile();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeInfoTab, setActiveInfoTab] = useState(0);
    const [activeParentTab, setActiveParentTab] = useState(0);
    const [activeLocationsSubTab, setActiveLocationsSubTab] = useState(0);

    useEffect(() => {
        document.title = 'SHARC - My Shows';
        return () => { document.title = 'SHARC - Shows Archive'; };
    }, []);

    useEffect(() => {
        async function load() {
            try {
                const token = await getIdToken();
                const res = await axios.get(`${config.API_URL}/user/shows`, {
                    headers: { Authorization: token }
                });
                setData(res.data);
                setLoading(false);
            } catch (e) {
                setError('Failed to load your shows.');
                setLoading(false);
            }
        }
        load();
    }, []);

    if (loading) return <div style={styles.message}>Loading your shows...</div>;
    if (error) return <div style={styles.errorMsg}>{error}</div>;
    if (!data) return null;

    const { user, stats, show_history, venues_visited, artists_watched, geography, show_map, followers, following } = data;

    const initials = user.display_name
        ? user.display_name.trim().split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2)
        : '?';
    const locationParts = [user.city, user.state, user.country].filter(Boolean);
    const canViewPublic = user.is_public && user.username;

    const mainStats = [
        { label: 'Total Shows', value: fmtNum(stats.total_shows) },
        { label: 'Total Performances', value: fmtNum(stats.total_performances) },
        { label: 'Total # of Artists', value: fmtNum(stats.total_artists) },
        { label: 'Total # of Stages', value: fmtNum(stats.total_stages) },
    ];
    // Performed/Worked hidden when 0 (Andy, 2026-09-22) — same as the
    // pre-redesign stat cards. Attended always shows.
    const showStats = [
        { label: 'Attended', value: fmtNum(stats.attended_shows) },
        ...(stats.performed_shows > 0 ? [{ label: 'Performed', value: fmtNum(stats.performed_shows) }] : []),
        ...(stats.worked_shows > 0 ? [{ label: 'Worked', value: fmtNum(stats.worked_shows) }] : []),
    ];
    const locationStats = [
        { label: 'Venues Visited', value: fmtNum(stats.venues_visited != null ? stats.venues_visited : venues_visited.length) },
        { label: 'Stages Visited', value: fmtNum(stats.total_stages) },
        { label: 'Countries Visited', value: fmtNum(stats.countries_visited) },
        { label: 'Cities Visited', value: fmtNum(stats.cities_visited) },
    ];

    return (
        <div style={{ width: '100%', minWidth: 0 }}>
            {/* ── Section 1: Display name + profile buttons ── */}
            <div style={isMobile ? { ...styles.section1, ...Smobile.section1 } : styles.section1}>
                <h1 style={styles.nameHeading}>{user.display_name || 'My Shows'}</h1>
                <div style={styles.section1Right}>
                    {canViewPublic && (
                        <Link to={`/u/${user.username}`} style={styles.ghostBtn}>View Public Profile</Link>
                    )}
                    <Link to="/profile" style={styles.ghostBtn}>Edit Profile</Link>
                </div>
            </div>

            {/* ── Section 2: Initials/meta left + info tabs right ── */}
            <div style={isMobile ? { ...styles.headerGrid, ...Smobile.headerGrid } : styles.headerGrid}>
                <div style={styles.headerLeft}>
                    <div style={styles.headerImageRow}>
                        {/* Interim placeholder until users can have an image */}
                        <div style={styles.initialsBox}>{initials}</div>
                        <div style={styles.headerMeta}>
                            {locationParts.length > 0 && (
                                <div style={styles.location}>{locationParts.join(', ')}</div>
                            )}
                            {user.member_id && user.member_name && (
                                <div style={styles.memberRecordLine}>
                                    Member Record – <Link to={`/members/${user.member_slug || user.member_id}`} style={styles.tableLink}>{user.member_name}</Link>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div style={styles.headerRight}>
                    {/* Mobile (2026-10-28): a single dropdown instead of a scrolling tab row */}
                    {isMobile ? (
                        <MobileTabSelect tabs={INFO_TABS} active={activeInfoTab} onChange={setActiveInfoTab} />
                    ) : (
                        <div style={styles.infoTabBar}>
                            {INFO_TABS.map((tab, i) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveInfoTab(i)}
                                    style={{ ...styles.infoTabBtn, ...(activeInfoTab === i ? styles.infoTabBtnActive : {}) }}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>
                    )}
                    <div style={isMobile ? { ...styles.infoTabContent, ...Smobile.infoTabContent } : styles.infoTabContent}>
                        <div style={styles.infoTabInner}>
                        {activeInfoTab === 0 && <StatBoxGrid items={mainStats} isMobile={isMobile} />}
                        {activeInfoTab === 1 && <StatBoxGrid items={showStats} isMobile={isMobile} />}
                        {activeInfoTab === 2 && <StatBoxGrid items={locationStats} isMobile={isMobile} />}
                        {activeInfoTab === 3 && (
                            <table style={styles.statsTable}>
                                <tbody>
                                    <tr>
                                        <td style={styles.statsTableLabel}>First Show</td>
                                        <td style={styles.statsTableValue}>{formatDate(stats.first_show_date)}</td>
                                    </tr>
                                    <tr>
                                        <td style={styles.statsTableLabel}>Most Recent Show</td>
                                        <td style={styles.statsTableValue}>{formatDate(stats.last_show_date)}</td>
                                    </tr>
                                    <tr>
                                        <td style={styles.statsTableLabel}>Term of Shows</td>
                                        <td style={styles.statsTableValue}>{computeTerm(stats.first_show_date, stats.last_show_date)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Section 3: Body tabs ── */}
            <div style={styles.tabsWrapper}>
                {/* Mobile (2026-10-28): a single dropdown instead of a scrolling tab row */}
                {isMobile ? (
                    <MobileTabSelect tabs={PARENT_TABS} active={activeParentTab} onChange={setActiveParentTab} />
                ) : (
                    <div style={styles.tabBar}>
                        {PARENT_TABS.map((tab, i) => (
                            <button key={tab} onClick={() => setActiveParentTab(i)} style={{ ...styles.tabBtn, ...(activeParentTab === i ? styles.tabBtnActive : {}) }}>{tab}</button>
                        ))}
                    </div>
                )}

                <div style={styles.tabContent}>
                    {activeParentTab === 0 && <ShowArchiveTab shows={show_history} />}
                    {activeParentTab === 1 && <ShowsByYearTab shows={show_history} />}
                    {activeParentTab === 2 && <ArtistsWatchedTab artists={artists_watched} paginationStyle="jump" />}
                    {activeParentTab === 3 && <VenuesVisitedTab venues={venues_visited} paginationStyle="jump" />}
                    {activeParentTab === 4 && (
                        <div>
                            <SubTabBar tabs={LOCATIONS_SUBTABS} active={activeLocationsSubTab} onChange={setActiveLocationsSubTab} />
                            <div style={styles.subTabContent}>
                                {activeLocationsSubTab === 0 && <ShowsMapTab showMap={show_map} />}
                                {activeLocationsSubTab === 1 && <GeographicBreakdownTab geography={geography} />}
                            </div>
                        </div>
                    )}
                    {activeParentTab === 5 && (
                        <FlyerHistoryTabs
                            entityType="user"
                            entityId={user.id}
                            shows={show_history}
                            searchByOptions={['Artist', 'Festival', 'Promoter', 'Venue']}
                            multiEventTabLabel="Tour/Schedule Flyer History"
                            showFlyerHistoryLabel="Show Flyer History"
                            paginationStyle="jump"
                            showMultiEventTab={false}
                        />
                    )}
                    {activeParentTab === 6 && <FollowersFollowingTab followers={followers} following={following} />}
                </div>
            </div>
        </div>
    );
}

// Values mirror ArtistDetail.js unless noted.
const styles = {
    // Section 1
    section1: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '16px' },
    section1Right: { display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 },
    nameHeading: { fontSize: '32px', fontWeight: 'bold', color: '#e0e0e0', margin: 0, lineHeight: 1.2 },
    // Matches ActionsDropdown.js's ghostBtn trigger (closed state)
    ghostBtn: { background: 'transparent', border: '1px solid #444', borderRadius: '4px', color: '#bb86fc', fontSize: '14px', padding: '7px 13px', cursor: 'pointer', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', textDecoration: 'none' },

    // Section 2 — left
    headerGrid: { display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '24px', marginBottom: '32px', alignItems: 'start' },
    headerLeft: { display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0 },
    headerImageRow: { display: 'flex', gap: '16px', alignItems: 'flex-start' },
    // Same footprint as ArtistDetail.js's artistImage/noImageBox; initials
    // treatment carried over from the pre-redesign My Shows header.
    initialsBox: { width: '160px', height: '160px', borderRadius: '8px', backgroundColor: '#bb86fc', color: '#121212', fontSize: '56px', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, letterSpacing: '2px' },
    headerMeta: { display: 'flex', flexDirection: 'column', gap: '4px', paddingTop: '4px', minWidth: 0 },
    location: { fontSize: '15px', color: '#aaa', marginBottom: '6px' },
    memberRecordLine: { fontSize: '13px', color: '#888' }, // MemberDetail.js's userRecordLine

    // Section 2 — right (info tabs)
    headerRight: { minWidth: 0 },
    // Tab bars (2026-09-23 scroll fix): overflowX: 'auto' silently forces
    // overflow-y to 'auto' too, and the old negative-marginBottom tab buttons
    // overflowed the bar by 1–2px vertically — so a mouse wheel over the bar
    // scrolled the bar itself (headers slid up) instead of the page. The
    // divider line is now an inset box-shadow, which paints beneath the
    // buttons, so the active underline covers it with zero overflow; plus an
    // explicit overflowY: 'hidden' so wheel events always chain to the page.
    infoTabBar: { display: 'flex', boxShadow: 'inset 0 -2px 0 #333', gap: '2px', overflowX: 'auto', overflowY: 'hidden' },
    infoTabBtn: { background: 'none', border: 'none', borderBottom: '2px solid transparent', padding: '10px 16px', fontSize: '14px', fontWeight: '600', color: '#777', cursor: 'pointer', whiteSpace: 'nowrap' },
    infoTabBtnActive: { color: '#bb86fc', borderBottom: '2px solid #bb86fc' },
    // display:flex + column so infoTabInner's auto vertical margins can
    // center each tab's content within the 160px min-height (2026-09-22).
    infoTabContent: { backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '16px', minHeight: '160px', maxHeight: '280px', overflowY: 'auto', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' },
    // Vertical centering via margin: auto rather than justifyContent: center —
    // auto margins collapse to 0 when content overflows, so a tall tab still
    // scrolls from its top instead of being clipped above the scroll area.
    infoTabInner: { margin: 'auto 0', width: '100%' },
    statGrid: { display: 'grid', gap: '12px' },
    statBox: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '20px 12px', textAlign: 'center', minWidth: 0 },
    statValue: { fontSize: '32px', fontWeight: 'bold', color: '#bb86fc', marginBottom: '8px', lineHeight: 1.1 },
    statLabel: { fontSize: '12px', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px' },
    statsTable: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
    statsTableLabel: { padding: '10px 16px 10px 0', color: '#aaa', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #2a2a2a', whiteSpace: 'nowrap', width: '1%' },
    statsTableValue: { padding: '10px 0', color: '#e0e0e0', fontWeight: 'bold', fontSize: '15px', borderBottom: '1px solid #2a2a2a' },

    // Section 3
    tabsWrapper: { marginTop: '8px', width: '100%', minWidth: 0 },
    tabBar: { display: 'flex', boxShadow: 'inset 0 -2px 0 #333', marginBottom: '0', overflowX: 'auto', overflowY: 'hidden', gap: '2px' },
    tabBtn: { background: 'none', border: 'none', borderBottom: '2px solid transparent', padding: '12px 20px', fontSize: '14px', fontWeight: '600', color: '#777', cursor: 'pointer', whiteSpace: 'nowrap', letterSpacing: '0.3px' },
    tabBtnActive: { color: '#bb86fc', borderBottom: '2px solid #bb86fc' },
    tabContent: { backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '24px', minHeight: '200px', width: '100%', minWidth: 0, boxSizing: 'border-box' },
    subTabBar: { display: 'flex', boxShadow: 'inset 0 -1px 0 #2e2e2e', gap: '2px', marginBottom: '0', overflowX: 'auto', overflowY: 'hidden' },
    subTabBtn: { background: 'none', border: 'none', borderBottom: '2px solid transparent', padding: '8px 16px', fontSize: '13px', fontWeight: '500', color: '#666', cursor: 'pointer', whiteSpace: 'nowrap' },
    subTabBtnActive: { color: '#bb86fc', borderBottom: '2px solid #bb86fc' },
    subTabContent: { paddingTop: '20px' },

    // Tables (Show Archive)
    tabSearch: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '10px 14px', fontSize: '14px', color: '#e0e0e0', width: '100%', marginBottom: '16px', boxSizing: 'border-box' },
    tableContainer: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
    th: { textAlign: 'left', padding: '10px 16px', color: '#aaa', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #333', whiteSpace: 'nowrap' },
    td: { padding: '10px 16px', color: '#e0e0e0', verticalAlign: 'middle' },
    trEven: { backgroundColor: 'transparent' },
    trOdd: { backgroundColor: '#1e1e1e' },
    tableLink: { color: '#bb86fc', textDecoration: 'none' },
    showCount: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '2px 8px', fontSize: '13px', color: '#bb86fc', fontWeight: 'bold' },
    roleAttended: { backgroundColor: '#1a3a1a', color: '#66bb6a', border: '1px solid #66bb6a', borderRadius: '4px', padding: '2px 8px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' },
    rolePerformed: { backgroundColor: '#1a2a3a', color: '#64b5f6', border: '1px solid #64b5f6', borderRadius: '4px', padding: '2px 8px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' },
    roleWorked: { backgroundColor: '#2b1a0d', color: '#ff9800', border: '1px solid #7a4200', borderRadius: '4px', padding: '2px 8px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' },

    // Geographic Breakdown (ArtistDetail.js)
    geoGroup: { marginBottom: '4px' },
    geoCountryRow: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', backgroundColor: '#222', borderRadius: '6px', cursor: 'pointer', userSelect: 'none', marginBottom: '2px' },
    geoCountryName: { flex: 1, fontSize: '14px', fontWeight: 'bold', color: '#e0e0e0' },
    geoStateBlock: { marginLeft: '16px', marginBottom: '2px' },
    geoStateRow: { display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', backgroundColor: '#1e1e1e', borderRadius: '5px', cursor: 'pointer', userSelect: 'none', marginBottom: '2px' },
    geoStateName: { flex: 1, fontSize: '13px', fontWeight: '600', color: '#ccc' },
    geoCityBlock: { marginLeft: '16px', marginBottom: '2px' },
    geoCityRow: { display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 12px', backgroundColor: '#1a1a1a', borderRadius: '4px', cursor: 'pointer', userSelect: 'none', marginBottom: '1px' },
    geoCityName: { flex: 1, fontSize: '13px', color: '#bbb' },
    geoVenueList: { marginLeft: '16px', paddingBottom: '4px' },
    geoVenueRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 12px', borderBottom: '1px solid #222' },
    geoVenueName: { fontSize: '13px', color: '#aaa', flex: 1 },
    geoChevron: { fontSize: '12px', color: '#555', width: '12px', flexShrink: 0 },
    geoCount: { fontSize: '12px', color: '#555', whiteSpace: 'nowrap' },

    // Misc
    emptyTab: { textAlign: 'center', color: '#666', padding: '40px 0', fontSize: '15px' },
    message: { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
    errorMsg: { textAlign: 'center', color: '#e57373', fontSize: '16px', padding: '60px 0' },
};

// Mobile overrides (<768px), merged at render time only — site-wide
// useIsMobile()/Smobile pattern. Never interleaved into `styles` above.
const Smobile = {
    // Name + two buttons wrap instead of squeezing the heading
    section1: { flexWrap: 'wrap', alignItems: 'flex-start' },
    // Initials/meta (left) + info tabs (right) stack
    headerGrid: { gridTemplateColumns: '1fr', gap: '20px' },
    // 2×2 stat boxes need more than the desktop 280px cap
    infoTabContent: { maxHeight: 'none' },
    statValue: { fontSize: '26px' },
};

export default MyShows;
